import React, { useLayoutEffect, useState } from 'react'
import { SearchAndFilter } from '../SearchAndFilter'
import {
  LearningCenterCategories,
  useLearningCenterContext,
} from '../../Context/LearningCenterContext'
import { groupByCycleOrSemesterAndWeek } from '../utils/learningCenterUtils'
import {
  FilterFileType,
  LearningCenterTabs,
} from '../../../utils/searchAndFilterEnums'
import LoadingProgress from '../../Elements/LoadingProgress'
import { Box, useTheme } from '@mui/material'
import { useScrollContext } from '../../Context/ScrollContext'
import { useMountEffect } from '../../../hooks/useMountEffect'
import { useLocation } from 'react-router'
import SearchResultsSection from './SearchResultSection'
import GroupedContentSection from './GroupedContentSection'
import WelcomeCenterTab from '../WelcomeCenterTab'
import PlaylistTabLC from './PlaylistTabLC'

interface LearningCenterTabContentProps {
  /** Notes whether fetchLearningCenterOptions is awaiting a response */
  loadingOptions?: boolean
  /** Notes whether fetchLearningCenterContent is awaiting a response */
  loadingContent?: boolean
}

const LearningCenterTabContent: React.FC<LearningCenterTabContentProps> = ({
  loadingOptions = false,
  loadingContent = false,
}) => {
  const theme = useTheme()
  const { pathname } = useLocation()

  const {
    assets,
    selectedTabKey,
    searchQuery,
    contentType,
    category,
    includeOnlyFavorites,
    welcomeCenterContent,
    updateLearningCenterAssetById,
  } = useLearningCenterContext()

  const { updateScrollY, prevScrollY, updateScrollPathnameForSection } =
    useScrollContext()

  const isChallengeProgram = selectedTabKey === LearningCenterTabs.Challenge
  useMountEffect(() => updateScrollPathnameForSection(pathname))

  useLayoutEffect(() => {
    updateScrollY(prevScrollY)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Filtering by content type, program, and category (strand)
   * are all determined by fetchLearningCenterContent.
   *
   */
  const groupedAssets = groupByCycleOrSemesterAndWeek(
    assets,
    isChallengeProgram
  )

  /**
   * If we are searching or filtering, display the content differently than
   * via accordions. Now it's via content cards.
   *
   * Filtering by challenge level has the added effect that, on load,
   * the state of the Challenge tab is always in search instead of semester.
   * Because of this, 'filtering' by level just switches the programType in
   * fetchLearningCenterContent instead of putting the page in a search state.
   */
  const isSearchingOrFiltering =
    !!searchQuery ||
    (!!category && category !== LearningCenterCategories.AllCategories) ||
    (!!contentType && contentType !== FilterFileType.AllTypes) ||
    includeOnlyFavorites

  /**
   * Leftover, arbitrary number to breakup the loading of content in search
   * state. This prevents a lag spike when rendering potentially hundreds of
   * cards for content. The View More button will maxLearningCenterContent to
   * currentLearningCenterMaxContentCount until it is greater than the total
   * assets.
   */
  const maxLearningCenterContent = 20
  const [
    currentLearningCenterMaxContentCount,
    setCurrentLearningCenterMaxContentCount,
  ] = useState(maxLearningCenterContent)

  const handleViewMoreLearningCenterContent = () => {
    setCurrentLearningCenterMaxContentCount(
      currentLearningCenterMaxContentCount + maxLearningCenterContent
    )
  }

  return (
    <Box sx={{ margin: { xs: theme.spacing(3, 2), sm: theme.spacing(3) } }}>
      {!loadingOptions &&
        !welcomeCenterContent &&
        selectedTabKey !== LearningCenterTabs.Playlist && <SearchAndFilter />}

      {loadingContent ? (
        <LoadingProgress />
      ) : (
        <>
          {isSearchingOrFiltering && (
            <SearchResultsSection
              assets={assets}
              isChallengeProgram={isChallengeProgram}
              selectedTabKey={selectedTabKey}
              maxItemsToDisplay={currentLearningCenterMaxContentCount}
              onViewMore={handleViewMoreLearningCenterContent}
              updateFavorite={updateLearningCenterAssetById}
            />
          )}

          {!isSearchingOrFiltering &&
            !welcomeCenterContent &&
            selectedTabKey !== LearningCenterTabs.Playlist && (
              <GroupedContentSection
                groupedAssets={groupedAssets}
                isChallengeProgram={isChallengeProgram}
              />
            )}

          {selectedTabKey === LearningCenterTabs.Playlist && <PlaylistTabLC />}

          {welcomeCenterContent &&
            selectedTabKey !== LearningCenterTabs.Playlist && (
              <WelcomeCenterTab content={assets} />
            )}
        </>
      )}
    </Box>
  )
}

export default LearningCenterTabContent
